<script>
  // import { createEventDispatcher } from 'svelte';
  import { format } from 'svelte-i18n';

  import { MergeView } from '@client/components/merge-view';
  import { ShowMore } from '@client/components/show-more';
  import { Spinner } from '@client/components/spinner';

  import { SignaturesService } from '@client/services/signatures';

  import { appStore } from '@client/stores/app';
  import { userStore } from '@client/stores/user';

  import { AUDIT_HISTORY_TYPES, USER_ROLES } from '@client/enums';

  import { formatDate } from '@common/utils/dates';
  import { formatBytes } from '@common/utils/numbers';
  import { detailedDiff } from '@common/utils/objects';

  import { NotesCell } from '../cell-formatters';

  export let row = {};

  let zoom = 0.7;

  let changes = {
    htmltemplate: {},
    replyhtmltemplate: {},
    params: {},
    generic: {}
  };

  const getUserSignature = async (signature) => {
    let email = $userStore.currentUser.email;

    if (signature.html) {
      return signature.html;
    }

    const data = {
      username: email,
      scratchblockname: signature.rulename,
      scratchtemplate: signature.htmltemplate,
      scratchparams: signature.params,
      testData: {
        testsender: email
      }
    };

    const { rendered } = await SignaturesService.getSignature(data);

    signature.loading = false;
    signature.html = rendered;

    return rendered;
  };

  if (row.note?.olditem) {
    delete row.note.olditem.highlights;
  }

  const diffResult = row.note?.olditem ? detailedDiff(row.note.olditem, row.note.item) : {};

  const onIframeLoad = function () {
    const zoom = $appStore.mobile ? 1.55 : 1.36;
    this.style.visibility = 'visible';
    this.style.opacity = 1;
    this.style.height = this.contentWindow.document.body.firstChild.clientHeight / zoom + 'px';
    this.style.width = this.contentWindow.document.body.firstChild.clientWidth / zoom + 'px';
  };

  $: zoom = $appStore.mobile ? 0.6 : 0.7;
</script>

<div class="audit-history-details">
  {#if row.type === AUDIT_HISTORY_TYPES.tagblocks.id && row.note}
    <div class="section">
      <div class="signature">
        {#await getUserSignature(row.note.item)}
          <div class="loading">
            <Spinner show inline />
          </div>
        {:then html}
          <iframe
            tabindex="-1"
            style="width: 100%; visibility: hidden; opacity: 0;"
            scrolling="no"
            on:load={onIframeLoad}
            title={row.note.item.blockname}
            srcdoc="<html><body><div style='display: inline-block; transform: scale({zoom},{zoom}); transform-origin: left top; -moz-transform:  scale({zoom},{zoom}); -moz-transform-origin: left top;'>{html ||
              row.note.item.htmltemplate ||
              row.note.item.replyhtmltemplate}</div></body></html>"
          />
        {:catch error}
          <p style="color: red">{error.message}</p>
        {/await}
      </div>
    </div>
  {/if}

  <div class="section">
    <div class="section-heading">{$format('label.SUMMARY')}</div>
    <div class="audit-history-details-header">
      <NotesCell bind:row />
      <small>
        {formatDate(row.timestamp, 'EEEE, MMMM d, yyyy @ h:mm a')} by
        <b>{row.user}</b>
      </small>
    </div>
  </div>

  {#if row.type === AUDIT_HISTORY_TYPES.outlookaddin.id && row.note?.data?.outlookAddinVersion}
    <div class="section">
      <div class="section-heading">{$format('label.SOURCE_DETAILS')}</div>
      <div>
        <b>{$format('label.OUTLOOK_ADDIN_VERSION')}</b>: {row.note.data.outlookAddinVersion}
      </div>
    </div>
  {/if}

  {#if row.type === AUDIT_HISTORY_TYPES.csv.id && row.note}
    <div class="section">
      <div class="section-heading">{$format('label.PUSHED_SIGNATURE')}</div>
      <div class="diff-viewer">
        <div class="viewer">
          <MergeView original="" modified={row.note.sigtemplate} readOnly changes={row.note.sigtemplate} />
        </div>
      </div>
    </div>
    {#if row.note?.sigdata?.data}
      <div class="section">
        <div class="section-heading">{$format('label.PUSHED_DATA')}</div>
        <div class="diff-viewer">
          <div class="viewer">
            <MergeView
              original=""
              modified={JSON.stringify(row.note.sigdata.data, null, 2)}
              readOnly
              changes={JSON.stringify(row.note.sigdata.data, null, 2)}
            />
          </div>
        </div>
      </div>
    {/if}
  {/if}

  {#if row.type === AUDIT_HISTORY_TYPES.images.id && row.note?.item}
    <img
      src="https://www.sendergen.com/v2/view/{row.note.item.hash}/thumbnail.gif?cache={new Date().getTime()}"
      alt={row.note.item.filename}
    />
  {/if}

  {#if row.type === AUDIT_HISTORY_TYPES.ads.id && row.note?.item?.previewURL}
    <div class="banner">
      <img src={row.note.item.previewURL} alt={row.note.item.filename} loading="lazy" />
      {#if row.note.item?.metadata}
        <div class="banner-details">
          <small>{row.note.item.metadata.origfile}</small>
          <small>
            {row.note.item.metadata.mimetype} -
            {row.note.item.metadata.imageinfo.width}x{row.note.item.metadata.imageinfo.height}
            ({formatBytes(row.note.item.metadata.filesize)})
          </small>
        </div>
      {/if}
    </div>
  {/if}

  {#if row.type === AUDIT_HISTORY_TYPES.signatures.id && row.note?.item}
    <div class="section">
      <div>
        {@html $format('label.BLOCKNAME')}
        : {row.note.item.blockname}
      </div>
      <div>
        {@html $format('label.PRIORITY')}
        : {row.note.item.priority}
      </div>
      <div>
        <b>{$format('label.ACTIVE')}</b>
        : {row.note.item.status}
      </div>
    </div>
  {/if}

  {#if (row.type === AUDIT_HISTORY_TYPES.manual.id || row.type === AUDIT_HISTORY_TYPES.outlookaddin.id) && row.note}
    {#if row.note?.data?.params}
      <div class="section">
        <div class="section-heading">{$format('label.FIELD_CHANGES')}</div>
        <div class="param-block">
          <b>{$format('label.USER_FIELDS')}</b>
          <ul>
            {#each row.note.data.params as param}
              <li>
                <b>{$format('label.PARAM')}:</b>
                {param.name} / <b>{$format('label.DEFAULT')}:</b>
                {param.default} / <b>{$format('label.OVERRIDE')}:</b>
                {param.forceoverride}
              </li>
            {/each}
          </ul>
        </div>
      </div>
    {/if}

    {#if row.note?.data?.roles}
      <div class="section">
        <div class="section-heading">{$format('label.ROLE_CHANGES')}</div>
        <div class="param-block">
          <ul class="role-list">
            {#each Object.entries(row.note.data.roles).filter(([, active]) => active) as [role]}
              <li>
                <span
                  class="role-badge"
                  style="background-color: {USER_ROLES[role]?.color || 'var(--color-gray-200)'};"
                />
                {$format(USER_ROLES[role]?.label?.long || `label.ROLE.${role.toUpperCase()}`)}
              </li>
            {/each}
          </ul>
        </div>
      </div>
    {/if}

    {#if row.note?.data?.usersettings && Object.keys(row.note.data.usersettings).length}
      <div class="section">
        <div class="section-heading">{$format('label.SETTINGS_CHANGES')}</div>
        <div class="param-block">
          <ul>
            {#each Object.entries(row.note.data.usersettings) as [key, value]}
              <li>
                <b>{key}</b>: {String(value)}
              </li>
            {/each}
          </ul>
        </div>
      </div>
    {/if}
  {/if}

  {#if row.type === AUDIT_HISTORY_TYPES.abm.id && row.note?.olditem && row.note?.olditem}
    <div class="section">
      <div class="diff-viewer">
        <div class="viewer">
          <MergeView
            original={JSON.stringify(row.note.olditem, null, 2)}
            modified={JSON.stringify(row.note.item, null, 2)}
            collapseUnchanged={true}
            readOnly
          />
        </div>
      </div>
    </div>
  {/if}

  {#if row.type === AUDIT_HISTORY_TYPES.tagblocks.id && row.note}
    {#if diffResult?.deleted?.params || diffResult?.added?.params || diffResult?.updated?.params}
      <div class="section">
        <div class="section-heading">{$format('label.FIELD_CHANGES')}</div>
        {#if diffResult?.added?.params}
          <div class="param-block">
            <b class="add">{$format('label.CREATED')}</b>
            <ul>
              {#each Object.keys(diffResult?.added?.params) as param}
                <li>
                  {@html $format('label.CREATED_NEW_FIELD', { values: { param } })}
                </li>
              {/each}
            </ul>
          </div>
        {/if}

        {#if diffResult?.updated?.params}
          <div class="param-block">
            <b class="update">{$format('label.UPDATED')}</b>
            <ul>
              {#each Object.keys(diffResult?.updated?.params) as param}
                <li>
                  {@html $format('label.UPDATED_FIELD', { values: { param } })}
                </li>
              {/each}
            </ul>
          </div>
        {/if}

        {#if diffResult?.deleted?.params}
          <div class="param-block">
            <b class="delete">{$format('label.DELETED')}</b>
            <ul>
              {#each Object.keys(diffResult?.deleted?.params) as param}
                <li>
                  {@html $format('label.DELETED_FIELD', { values: { param } })}
                </li>
              {/each}
            </ul>
          </div>
        {/if}

        {#if row.note.olditem?.params}
          <ShowMore collapsedText="View Param Changes" expandedText="Hide Param Changes">
            <pre>{JSON.stringify(detailedDiff(row.note.olditem.params, row.note.item.params), null, 2)}</pre>
          </ShowMore>
        {/if}
      </div>
    {/if}

    {#if row.note?.olditem}
      {#if diffResult?.updated?.htmltemplate}
        <div class="section">
          <div class="section-heading">
            {$format('label.TEMPLATE_CHANGES')}
            <div class="diff-changes">
              <span class="additions">+{changes.htmltemplate?.additions || 0}</span>
              <span class="deletions">-{changes.htmltemplate?.deletions || 0}</span>
            </div>
          </div>
          <div class="diff-viewer">
            <div class="viewer">
              <MergeView
                bind:original={row.note.olditem.htmltemplate}
                bind:modified={row.note.item.htmltemplate}
                collapseUnchanged={true}
                readOnly
                bind:changes={changes.htmltemplate}
              />
            </div>
          </div>
        </div>
      {/if}

      {#if diffResult?.updated?.replyhtmltemplate}
        <div class="section">
          <div class="section-heading">
            {$format('label.REPLY_TEMPLATE_CHANGES')}
            <div class="diff-changes">
              <span class="additions">+{changes.replyhtmltemplate?.additions || 0}</span>
              <span class="deletions">-{changes.replyhtmltemplate?.deletions || 0}</span>
            </div>
          </div>
          <div class="diff-viewer">
            <div class="viewer">
              <MergeView
                bind:original={row.note.olditem.replyhtmltemplate}
                bind:modified={row.note.item.replyhtmltemplate}
                collapseUnchanged={true}
                readOnly
                bind:changes={changes.replyhtmltemplate}
              />
            </div>
          </div>
        </div>
      {/if}
    {:else}
      {#if row.note?.item?.htmltemplate}
        <div class="section">
          <div class="section-heading">{$format('label.TEMPLATE_CHANGES')}</div>
          <div class="diff-viewer">
            <div class="viewer">
              <MergeView
                original=""
                bind:modified={row.note.item.htmltemplate}
                readOnly
                bind:changes={row.note.item.htmltemplate}
              />
            </div>
          </div>
        </div>
      {/if}

      {#if row.note?.item?.replyhtmltemplate}
        <div class="section">
          <div class="section-heading">{$format('label.REPLY_TEMPLATE_CHANGES')}</div>
          <div class="diff-viewer">
            <div class="viewer">
              <MergeView
                original=""
                bind:modified={row.note.item.replyhtmltemplate}
                readOnly
                bind:changes={row.note.item.replyhtmltemplate}
              />
            </div>
          </div>
        </div>
      {/if}
    {/if}
  {/if}

  <ShowMore collapsedText="View History Object" expandedText="Hide History Object">
    <pre style="word-break: all;">{JSON.stringify(row, null, 2)}</pre>
  </ShowMore>
</div>

<style lang="scss" src="./row-expansion.scss"></style>
